import React, { useState, useEffect } from 'react';
import { fetchGraduates, fetchGraduateStats, fetchCenters } from '../services/dashboard';
import BarChartComponent from '../components/BarChartComponent';
import CurveChartComponent from '../components/CurveChartComponent';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGraduationCap, faBuilding, faChartLine, faSearch, faFilter, faDownload, faCalendarCheck } from '@fortawesome/free-solid-svg-icons';
import { exportToExcel } from '../utils/exportToExcel';

const GraduatesDashboard = () => {
    const [graduates, setGraduates] = useState([]);
    const [stats, setStats] = useState(null);
    const [organizations, setOrganizations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filters, setFilters] = useState({
        organizationId: '',
        specializationId: '',
        search: '',
        startDate: '',
        endDate: ''
    });

    useEffect(() => {
        loadData();
        loadOrganizations();
    }, []);

    const loadData = async () => {
        try {
            setLoading(true);
            const [graduatesData, statsData] = await Promise.all([
                fetchGraduates(filters),
                fetchGraduateStats(filters.organizationId || null)
            ]);
            setGraduates(graduatesData.data || []);
            setStats(statsData.stats || null);
        } catch (error) {
            console.error('Error loading graduates data:', error);
        } finally {
            setLoading(false);
        }
    };

    const loadOrganizations = async () => {
        try {
            const centersData = await fetchCenters();
            setOrganizations(centersData.centers || []);
        } catch (error) {
            console.error('Error loading organizations:', error);
        }
    };

    const handleFilterChange = (field, value) => {
        setFilters(prev => ({ ...prev, [field]: value }));
    };

    const applyFilters = () => {
        loadData();
    };

    const resetFilters = () => {
        setFilters({
            organizationId: '',
            specializationId: '',
            search: '',
            startDate: '',
            endDate: ''
        });
        setTimeout(() => loadData(), 100);
    };

    const handleExport = () => {
        const columns = [
            { header: 'الاسم', key: '_fullName' },
            { header: 'المركز', key: 'school.name' },
            { header: 'التخصص', key: 'specialization.name' },
            { header: 'البريد الإلكتروني', key: 'email' },
            { header: 'تاريخ التسجيل', key: '_createdAt' }
        ];

        const dataWithComputed = graduates.map(g => ({
            ...g,
            _fullName: `${g.first_name || ''} ${g.middle_name || ''} ${g.last_name || ''}`.trim(),
            _createdAt: g.createdAt ? new Date(g.createdAt).toLocaleDateString('ar-EG') : ''
        }));

        exportToExcel(dataWithComputed, columns, `graduates_${new Date().toISOString().split('T')[0]}`);
    };

    // Prepare chart data
    const orgChartData = stats?.byOrganization?.map(org => ({
        name: org.school?.name || 'غير محدد',
        value: parseInt(org.count) || 0
    })) || [];

    const specializationChartData = stats?.bySpecialization?.map(spec => ({
        name: spec.specialization?.name || 'غير محدد',
        value: parseInt(spec.count) || 0
    })) || [];

    const monthlyChartData = stats?.byMonth?.map(m => ({
        month: m.month || '',
        performance: parseInt(m.count) || 0
    })) || [];

    return (
        <div className="min-h-screen p-6" style={{ background: 'linear-gradient(135deg, #1a1f3a 0%, #2d3347 100%)' }}>
            {/* Header */}
            <div className="mb-8">
                <h1 className="text-4xl font-bold text-white mb-2 flex items-center gap-3">
                    <FontAwesomeIcon icon={faGraduationCap} className="text-yellow-400" />
                    لوحة الخريجين
                </h1>
                <p className="text-gray-400">عرض وتحليل بيانات الخريجين من مراكز التدريب</p>
            </div>

            {/* Statistics Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                <div className="rounded-2xl p-6" style={{ background: '#2d3347', boxShadow: '0 2px 12px #0004' }}>
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-gray-400 text-sm mb-1">إجمالي الخريجين</p>
                            <h3 className="text-3xl font-bold text-yellow-400">{stats?.total || 0}</h3>
                        </div>
                        <FontAwesomeIcon icon={faGraduationCap} className="text-4xl text-yellow-400 opacity-20" />
                    </div>
                </div>

                <div className="rounded-2xl p-6" style={{ background: '#2d3347', boxShadow: '0 2px 12px #0004' }}>
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-gray-400 text-sm mb-1">عدد المراكز</p>
                            <h3 className="text-3xl font-bold text-blue-400">{stats?.byOrganization?.length || 0}</h3>
                        </div>
                        <FontAwesomeIcon icon={faBuilding} className="text-4xl text-blue-400 opacity-20" />
                    </div>
                </div>

                <div className="rounded-2xl p-6" style={{ background: '#2d3347', boxShadow: '0 2px 12px #0004' }}>
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-gray-400 text-sm mb-1">عدد التخصصات</p>
                            <h3 className="text-3xl font-bold text-green-400">{stats?.bySpecialization?.length || 0}</h3>
                        </div>
                        <FontAwesomeIcon icon={faChartLine} className="text-4xl text-green-400 opacity-20" />
                    </div>
                </div>

                <div className="rounded-2xl p-6" style={{ background: '#2d3347', boxShadow: '0 2px 12px #0004' }}>
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-gray-400 text-sm mb-1">الخريجون هذا الشهر</p>
                            <h3 className="text-3xl font-bold text-purple-400">
                                {stats?.graduatesThisMonth || 0}
                            </h3>
                        </div>
                        <FontAwesomeIcon icon={faCalendarCheck} className="text-4xl text-purple-400 opacity-20" />
                    </div>
                </div>
            </div>

            {/* Filters Panel */}
            <div className="rounded-2xl p-6 mb-8" style={{ background: '#2d3347', boxShadow: '0 2px 12px #0004' }}>
                <h2 className="text-xl font-bold text-yellow-400 mb-4 flex items-center gap-2">
                    <FontAwesomeIcon icon={faFilter} />
                    تصفية البيانات
                </h2>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
                    <div>
                        <label className="block text-gray-300 text-sm mb-2">المركز</label>
                        <select
                            className="w-full px-4 py-2 rounded-lg bg-gray-700 text-white border border-gray-600 focus:border-yellow-400 focus:outline-none"
                            value={filters.organizationId}
                            onChange={(e) => handleFilterChange('organizationId', e.target.value)}
                        >
                            <option value="">الكل</option>
                            {organizations.map(org => (
                                <option key={org.id} value={org.id}>{org.name}</option>
                            ))}
                        </select>
                    </div>

                    <div>
                        <label className="block text-gray-300 text-sm mb-2">من تاريخ</label>
                        <input
                            type="date"
                            className="w-full px-4 py-2 rounded-lg bg-gray-700 text-white border border-gray-600 focus:border-yellow-400 focus:outline-none"
                            value={filters.startDate}
                            onChange={(e) => handleFilterChange('startDate', e.target.value)}
                        />
                    </div>

                    <div>
                        <label className="block text-gray-300 text-sm mb-2">إلى تاريخ</label>
                        <input
                            type="date"
                            className="w-full px-4 py-2 rounded-lg bg-gray-700 text-white border border-gray-600 focus:border-yellow-400 focus:outline-none"
                            value={filters.endDate}
                            onChange={(e) => handleFilterChange('endDate', e.target.value)}
                        />
                    </div>

                    <div className="lg:col-span-3">
                        <label className="block text-gray-300 text-sm mb-2">بحث (الاسم)</label>
                        <input
                            type="text"
                            placeholder="ابحث بالاسم..."
                            className="w-full px-4 py-2 rounded-lg bg-gray-700 text-white border border-gray-600 focus:border-yellow-400 focus:outline-none"
                            value={filters.search}
                            onChange={(e) => handleFilterChange('search', e.target.value)}
                        />
                    </div>
                </div>

                <div className="flex gap-3">
                    <button
                        onClick={applyFilters}
                        className="px-6 py-2 bg-yellow-400 text-gray-900 rounded-lg font-bold hover:bg-yellow-500 transition-colors flex items-center gap-2"
                    >
                        <FontAwesomeIcon icon={faSearch} />
                        تطبيق
                    </button>
                    <button
                        onClick={resetFilters}
                        className="px-6 py-2 bg-gray-600 text-white rounded-lg font-bold hover:bg-gray-700 transition-colors"
                    >
                        إعادة تعيين
                    </button>
                    <button
                        onClick={handleExport}
                        className="px-6 py-2 bg-green-600 text-white rounded-lg font-bold hover:bg-green-700 transition-colors flex items-center gap-2 mr-auto"
                    >
                        <FontAwesomeIcon icon={faDownload} />
                        تصدير Excel
                    </button>
                </div>
            </div>

            {/* Charts */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
                <BarChartComponent
                    data={orgChartData}
                    title="الخريجون حسب المركز"
                    dataKey="value"
                    nameKey="name"
                    loading={loading}
                    height={350}
                />

                <BarChartComponent
                    data={specializationChartData}
                    title="الخريجون حسب التخصص"
                    dataKey="value"
                    nameKey="name"
                    loading={loading}
                    height={350}
                />
            </div>

            <div className="mb-8">
                <CurveChartComponent
                    data={monthlyChartData}
                    title="الخريجون الشهريون (آخر 12 شهر)"
                    dataKeys={['performance']}
                    loading={loading}
                    height={350}
                />
            </div>

            {/* Data Table */}
            <div className="rounded-2xl overflow-hidden" style={{ background: '#2d3347', boxShadow: '0 2px 12px #0004' }}>
                <div className="p-6">
                    <h2 className="text-xl font-bold text-yellow-400 mb-4">قائمة الخريجين</h2>

                    {loading ? (
                        <div className="text-center text-gray-400 py-12">جاري التحميل...</div>
                    ) : graduates.length === 0 ? (
                        <div className="text-center text-gray-400 py-12">لا توجد بيانات متاحة</div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead>
                                    <tr className="border-b border-gray-600">
                                        <th className="text-right p-3 text-gray-300 font-semibold">#</th>
                                        <th className="text-right p-3 text-gray-300 font-semibold">الاسم</th>
                                        <th className="text-right p-3 text-gray-300 font-semibold">المركز</th>
                                        <th className="text-right p-3 text-gray-300 font-semibold">التخصص</th>
                                        <th className="text-right p-3 text-gray-300 font-semibold">البريد الإلكتروني</th>
                                        <th className="text-right p-3 text-gray-300 font-semibold">تاريخ التسجيل</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {graduates.map((graduate, index) => (
                                        <tr key={graduate.id} className="border-b border-gray-700 hover:bg-gray-700 transition-colors">
                                            <td className="p-3 text-gray-300">{index + 1}</td>
                                            <td className="p-3 text-white font-medium">
                                                {graduate.first_name} {graduate.middle_name} {graduate.last_name}
                                            </td>
                                            <td className="p-3 text-gray-300">{graduate.school?.name || '-'}</td>
                                            <td className="p-3 text-gray-300">{graduate.specialization?.name || '-'}</td>
                                            <td className="p-3 text-gray-300">{graduate.email || '-'}</td>
                                            <td className="p-3 text-gray-300">
                                                {new Date(graduate.createdAt).toLocaleDateString('ar-EG')}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default GraduatesDashboard;
